import { useCallback, useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  peerLeaderPostSessionApi,
  type PeerLeaderPostSessionBundle,
} from '../services/api/peerLeaderPostSession';
import type {
  AbsentReason,
  AttendanceMarkingStatus,
  EngagementRatingInput,
  ParticipantAttendanceInput,
} from '../services/api/coachPortal';
import type { PostSessionCommitment } from '../services/api/coachPostSession';
import {
  FEEDBACK_SUBMITTED_FLAG,
  isFeedbackSubmitted,
} from '../config/peerLeaderFeedbackQuestionSets';
import { useAiInsightPolling } from './useAiInsightPolling';

/**
 * MED-817 / E4.5 — data + mutation hook for the Peer Leader post-session screen.
 *
 * Mirrors `useCoachPostSession` (the peer leader gets the full coach
 * post-session structure) but targets the peer-leader endpoints and adds the
 * dual-role specifics:
 *   - Attendance defaults the leader's OWN row to Present (AC5) with blank
 *     engagement/notes, writing through the shared attendance backend (IV2).
 *   - Feedback Save Draft vs Submit is distinguished by the `__submitted` flag
 *     carried in the responses blob (AC10/AC12) — see the config module.
 *   - Commitments (MED-1216): explicit per-row Save (no debounced auto-save),
 *     with no-blank enforcement. The leader's row is self-authored server-side
 *     and self-only (MED-1133/E4.5a) — no coach fill-in / ownership lock here.
 */

export interface AttendanceDraft {
  status: AttendanceMarkingStatus | null;
  absent_reason: AbsentReason | null;
  rating: number | null;
  coach_notes: string | null;
  // MED-1191 AC8: seeded from the server's derived status_auto_populated (see
  // useCoachPostSession). The leader's own row is never auto-populated (the
  // server excludes it), so this stays false for the "You" row (AC5).
  autoPopulated: boolean;
}

export type AttendanceDraftMap = Record<string, AttendanceDraft>;
export type FeedbackResponseMap = Record<string, string>;

interface UsePeerLeaderPostSessionResult {
  bundle: PeerLeaderPostSessionBundle | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;

  attendance: AttendanceDraftMap;
  setAttendanceForParticipant: (participantId: string, next: AttendanceDraft) => void;
  saveAttendance: () => Promise<boolean>;
  savingAttendance: boolean;

  feedbackResponses: FeedbackResponseMap;
  setFeedbackResponse: (key: string, value: string) => void;
  saveFeedbackDraft: () => Promise<boolean>;
  submitFeedback: () => Promise<boolean>;
  savingFeedback: boolean;
  feedbackSubmittedAt: string | null;

  // MED-1216 — server-truth commitment rows + explicit per-row save.
  commitments: PostSessionCommitment[];
  saveCommitment: (participantId: string, text: string) => Promise<boolean>;
  savingParticipantId: string | null;
}

/**
 * Map the participant row's persisted `current_status` to the three-way working
 * enum. Anything that isn't attended/partial/absent is "not set yet" (null).
 */
function toAttendanceMarking(status: string): AttendanceMarkingStatus | null {
  if (status === 'attended' || status === 'partial' || status === 'absent') return status;
  if (status === 'present') return 'attended';
  return null;
}

export function usePeerLeaderPostSession(
  sessionId: string | null,
  options: { previewBundle?: PeerLeaderPostSessionBundle } = {},
): UsePeerLeaderPostSessionResult {
  // MED-1040 — DEV preview seam: seed from a supplied bundle, skip the network.
  const previewBundle = options.previewBundle;
  const [bundle, setBundle] = useState<PeerLeaderPostSessionBundle | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [attendance, setAttendance] = useState<AttendanceDraftMap>({});
  const [feedbackResponses, setFeedbackResponses] = useState<FeedbackResponseMap>({});

  const [savingAttendance, setSavingAttendance] = useState<boolean>(false);
  const [savingFeedback, setSavingFeedback] = useState<boolean>(false);
  // MED-1216 — which participant's commitment row is saving (the leader's own).
  const [savingParticipantId, setSavingParticipantId] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Initial load + refresh
  // ---------------------------------------------------------------------------

  const loadBundle = useCallback(async () => {
    const applyBundle = (data: PeerLeaderPostSessionBundle) => {
      setBundle(data);

      const ratingByParticipant = new Map<string, number>(
        data.engagement_ratings.map((r) => [r.participant_id, r.rating]),
      );
      const attMap: AttendanceDraftMap = {};
      for (const p of data.participants) {
        const marking = toAttendanceMarking(p.current_status);
        const isLeader = p.user_id === data.leader_user_id;
        attMap[p.user_id] = {
          // AC5: the leader's own row defaults to Present when no status is set
          // yet. Engagement + Notes stay blank — a genuine self-reflection.
          status: marking ?? (isLeader ? 'attended' : null),
          absent_reason: p.current_absent_reason,
          rating: ratingByParticipant.get(p.user_id) ?? null,
          coach_notes: p.current_coach_notes,
          autoPopulated: p.status_auto_populated,
        };
      }
      setAttendance(attMap);

      const fb: FeedbackResponseMap = {};
      if (data.feedback?.responses) {
        for (const [k, v] of Object.entries(data.feedback.responses)) fb[k] = v;
      }
      setFeedbackResponses(fb);
      // MED-1216 — commitment rows are read directly from `bundle.commitments`;
      // the card owns the leader's per-row edit draft. No draft map to seed.
    };

    // DEV preview: seed from the fixture, skip the network entirely.
    if (previewBundle) {
      applyBundle(previewBundle);
      setError(null);
      setLoading(false);
      return;
    }

    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await peerLeaderPostSessionApi.getBundle(sessionId);
      applyBundle(data);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load post-session data';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [sessionId, previewBundle]);

  useEffect(() => {
    void loadBundle();
  }, [loadBundle]);

  // ---------------------------------------------------------------------------
  // MED-1239 — auto-resolve async AI insights without a hard refresh
  // ---------------------------------------------------------------------------

  /**
   * Refetch the bundle and merge ONLY the Session Themes envelope into state.
   * Like the coach hook, this deliberately bypasses `applyBundle` / `loadBundle`
   * so the leader's in-progress attendance/feedback/commitment edits are never
   * reseeded from server truth on a poll tick (AC5). Poll failures are swallowed.
   *
   * Only `themes` is tracked here: the Airtime Balance card was made coach-only
   * in MED-1207 (D16), so the peer-leader page no longer renders it. The peer
   * bundle still carries `airtime`, but with no card there is no stuck loader to
   * resolve — tracking it would only keep polling for an invisible envelope.
   */
  const pollAiEnvelopes = useCallback(async () => {
    if (!sessionId) return;
    try {
      const fresh = await peerLeaderPostSessionApi.getBundle(sessionId);
      setBundle((prev) => (prev ? { ...prev, themes: fresh.themes } : prev));
    } catch {
      // Intentionally ignored — a transient poll failure must not toast/blank.
    }
  }, [sessionId]);

  const aiInsightsPending = bundle?.themes?.status === 'pending';
  useAiInsightPolling({
    enabled: !previewBundle && !!sessionId,
    pending: !!aiInsightsPending,
    poll: pollAiEnvelopes,
  });

  // ---------------------------------------------------------------------------
  // Attendance & engagement
  // ---------------------------------------------------------------------------

  const setAttendanceForParticipant = useCallback(
    (participantId: string, next: AttendanceDraft) => {
      setAttendance((prev) => ({ ...prev, [participantId]: next }));
    },
    [],
  );

  const saveAttendance = useCallback(async (): Promise<boolean> => {
    if (!sessionId || !bundle) return false;

    const records: ParticipantAttendanceInput[] = [];
    const ratings: EngagementRatingInput[] = [];

    for (const p of bundle.participants) {
      const draft = attendance[p.user_id];
      if (!draft || !draft.status) continue;
      const isLeader = p.user_id === bundle.leader_user_id;
      const trimmedNotes = draft.coach_notes?.trim() ?? '';
      records.push({
        user_id: p.user_id,
        status: draft.status,
        absent_reason:
          draft.status === 'partial' || draft.status === 'absent'
            ? draft.absent_reason
            : null,
        coach_notes: trimmedNotes === '' ? null : trimmedNotes,
      });
      // MED-1163 AC4: never write an engagement rating for the leader's own
      // row — the leader is not asked to rate their own engagement. Excluding
      // it from the submitted set also self-heals any stale leader rating: the
      // markAttendance save path deletes ratings not present in this payload.
      if (
        !isLeader &&
        (draft.status === 'attended' || draft.status === 'partial') &&
        draft.rating !== null &&
        draft.rating >= 1 &&
        draft.rating <= 5
      ) {
        ratings.push({ participant_id: p.user_id, rating: draft.rating });
      }
    }

    if (records.length === 0) {
      notifications.show({
        color: 'yellow',
        message: 'Select attendance for at least one participant before saving.',
      });
      return false;
    }

    setSavingAttendance(true);
    try {
      await peerLeaderPostSessionApi.markAttendance(sessionId, {
        attendance: records,
        send_surveys: true,
        engagement_ratings: ratings,
      });
      notifications.show({ color: 'green', message: 'Attendance saved.' });
      await loadBundle();
      return true;
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof Error ? e.message : 'Failed to save attendance.',
      });
      return false;
    } finally {
      setSavingAttendance(false);
    }
  }, [sessionId, bundle, attendance, loadBundle]);

  // ---------------------------------------------------------------------------
  // Feedback form — Save Draft vs Submit distinguished by the __submitted flag
  // ---------------------------------------------------------------------------

  const setFeedbackResponse = useCallback((key: string, value: string) => {
    setFeedbackResponses((prev) => ({ ...prev, [key]: value }));
  }, []);

  const persistFeedback = useCallback(
    async (submit: boolean): Promise<boolean> => {
      if (!sessionId) return false;
      setSavingFeedback(true);
      try {
        const responses: FeedbackResponseMap = { ...feedbackResponses };
        if (submit) {
          responses[FEEDBACK_SUBMITTED_FLAG] = 'true';
        } else {
          // A saved draft must not carry the submitted flag.
          delete responses[FEEDBACK_SUBMITTED_FLAG];
        }
        await peerLeaderPostSessionApi.upsertFeedback(sessionId, { responses });
        notifications.show({
          color: 'green',
          message: submit ? 'Feedback submitted.' : 'Feedback draft saved.',
        });
        await loadBundle();
        return true;
      } catch (e) {
        notifications.show({
          color: 'red',
          message: e instanceof Error ? e.message : 'Failed to save feedback.',
        });
        return false;
      } finally {
        setSavingFeedback(false);
      }
    },
    [sessionId, feedbackResponses, loadBundle],
  );

  const saveFeedbackDraft = useCallback(() => persistFeedback(false), [persistFeedback]);
  const submitFeedback = useCallback(() => persistFeedback(true), [persistFeedback]);

  // ---------------------------------------------------------------------------
  // Commitments — explicit per-row save (MED-1216, self-only)
  // ---------------------------------------------------------------------------

  /**
   * Save the peer leader's own commitment (self-only; the server drops any
   * non-self row). No-blank is enforced by the disabled Save button and the
   * server backstop (400). No ownership lock applies — the leader's row is
   * their own to edit.
   */
  const saveCommitment = useCallback(
    async (participantId: string, text: string): Promise<boolean> => {
      if (!sessionId) return false;
      setSavingParticipantId(participantId);
      try {
        const response = await peerLeaderPostSessionApi.upsertCommitments(sessionId, {
          commitments: [{ participant_user_id: participantId, commitment_text: text }],
        });
        setBundle((prev) => (prev ? { ...prev, commitments: response.commitments } : prev));
        return true;
      } catch (e) {
        notifications.show({
          color: 'red',
          message: e instanceof Error ? e.message : 'Failed to save commitment.',
        });
        return false;
      } finally {
        setSavingParticipantId(null);
      }
    },
    [sessionId],
  );

  // ---------------------------------------------------------------------------
  // Public surface
  // ---------------------------------------------------------------------------

  const feedbackSubmittedAt =
    bundle?.feedback && isFeedbackSubmitted(bundle.feedback.responses)
      ? bundle.feedback.submitted_at
      : null;

  return {
    bundle,
    loading,
    error,
    refresh: loadBundle,

    attendance,
    setAttendanceForParticipant,
    saveAttendance,
    savingAttendance,

    feedbackResponses,
    setFeedbackResponse,
    saveFeedbackDraft,
    submitFeedback,
    savingFeedback,
    feedbackSubmittedAt,

    commitments: bundle?.commitments ?? [],
    saveCommitment,
    savingParticipantId,
  };
}
